/**
 * T&D — Test calculations for Tu Radar D1.
 * MBTI (16 preguntas A/B) + Skills radar (20 preguntas 1-5) + Route suggestion.
 */

/**
 * Calculate MBTI type from answers.
 * @param {Array<{questionId: number, value: string}>} answers — value is E/I/S/N/T/F/J/P
 * @returns {string} 4-letter type, e.g. "ENFP"
 */
export function calculateMBTI(answers) {
  const scores = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };

  answers.forEach(answer => {
    if (scores[answer.value] !== undefined) {
      scores[answer.value]++;
    }
  });

  return [
    scores.E >= scores.I ? 'E' : 'I',
    scores.S >= scores.N ? 'S' : 'N',
    scores.T >= scores.F ? 'T' : 'F',
    scores.J >= scores.P ? 'J' : 'P'
  ].join('');
}

/**
 * Calculate skills radar from answers.
 * @param {Array<{questionId: number, skill: string, value: number}>} answers — value is 1-5
 * @returns {Object} { comunicacion: 1-10, creatividad: 1-10, ... }
 */
export function calculateRadar(answers) {
  const categories = {
    comunicacion: 0, creatividad: 0, tecnologia: 0, organizacion: 0,
    ventas: 0, liderazgo: 0, diseno: 0, idiomas: 0,
    analisis: 0, ejecucion: 0
  };
  const counts = {
    comunicacion: 0, creatividad: 0, tecnologia: 0, organizacion: 0,
    ventas: 0, liderazgo: 0, diseno: 0, idiomas: 0,
    analisis: 0, ejecucion: 0
  };

  answers.forEach(answer => {
    if (categories[answer.skill] !== undefined) {
      categories[answer.skill] += answer.value;
      counts[answer.skill]++;
    }
  });

  // Average per category, scaled to 1-10
  Object.keys(categories).forEach(key => {
    if (counts[key] > 0) {
      categories[key] = Math.round((categories[key] / counts[key]) * 2);
    }
  });

  return categories;
}

/**
 * Suggest a route based on MBTI type and radar scores.
 * @param {string} mbtiType — 4-letter MBTI
 * @param {Object} radar — { comunicacion, creatividad, ... } scores 1-10
 * @returns {{ primary: string, secondary: string, scores: Object }}
 */
export function suggestRoute(mbtiType, radar) {
  // RUTA 1: VENDER TU TIEMPO (Freelance/Servicios)
  const rutaTiempo =
    (radar.ejecucion || 0) +
    (radar.organizacion || 0) +
    (radar.tecnologia || 0) +
    (radar.diseno || 0);

  // RUTA 2: VENDER PRODUCTOS (Digitales/Físicos)
  const rutaProductos =
    (radar.creatividad || 0) +
    (radar.ventas || 0) +
    (radar.analisis || 0) +
    (radar.diseno || 0);

  // RUTA 3: CONSTRUIR ACTIVOS (Contenido/Comunidad)
  const rutaActivos =
    (radar.comunicacion || 0) +
    (radar.creatividad || 0) +
    (radar.liderazgo || 0) +
    (radar.idiomas || 0);

  const routeScores = { tiempo: rutaTiempo, productos: rutaProductos, activos: rutaActivos };

  // Adjust by MBTI
  if (mbtiType.includes('E') && mbtiType.includes('N')) {
    return { primary: 'activos', secondary: 'productos', scores: routeScores };
  }
  if (mbtiType.includes('I') && mbtiType.includes('S')) {
    return { primary: 'tiempo', secondary: 'productos', scores: routeScores };
  }
  if (mbtiType.includes('T') && mbtiType.includes('J')) {
    return { primary: 'productos', secondary: 'tiempo', scores: routeScores };
  }

  // Fallback: highest score wins
  const sorted = Object.entries(routeScores).sort((a, b) => b[1] - a[1]);
  return { primary: sorted[0][0], secondary: sorted[1][0], scores: routeScores };
}

// Route labels for display
export const ROUTE_LABELS = {
  tiempo: { name: 'Vender tu tiempo', emoji: '⏰', desc: 'Freelance, consultoría, servicios. Monetiza tus habilidades directamente.' },
  productos: { name: 'Vender productos', emoji: '📦', desc: 'E-commerce, cursos, productos digitales o físicos. Crea algo que se venda sin ti.' },
  activos: { name: 'Construir activos', emoji: '🏗️', desc: 'Contenido, comunidad, marca personal. Construye una audiencia que genere ingresos.' },
};

// Skill labels for radar chart
export const SKILL_LABELS = {
  comunicacion: 'Comunicación',
  creatividad: 'Creatividad',
  tecnologia: 'Tecnología',
  organizacion: 'Organización',
  ventas: 'Ventas',
  liderazgo: 'Liderazgo',
  diseno: 'Diseño',
  idiomas: 'Idiomas',
  analisis: 'Análisis',
  ejecucion: 'Ejecución',
};

/**
 * Calculate match % between a forma and user's radar skills.
 * @param {Object} forma — { required_skills: ['diseno','ventas',...] }
 * @param {Object} userRadar — { comunicacion: 8, creatividad: 6, ... } (1-10 scale)
 * @returns {number} 0-100
 */
export function calculateFormMatch(forma, userRadar) {
  if (!userRadar || !forma.required_skills) return 50;
  const required = forma.required_skills;
  let totalScore = 0;
  const maxScore = required.length * 10;
  required.forEach(skill => {
    totalScore += (userRadar[skill] || 5);
  });
  return Math.round((totalScore / maxScore) * 100);
}
